import classNames from 'classnames';
import {
  Avatar,
  Box,
  Header,
  IconButton,
  Menu,
  MenuItem,
  Scroll,
  Text,
  as,
  color,
  config,
} from 'folds';
import type { IContent, MatrixEvent, Room } from '$types/matrix-sdk';
import { getAvatarUrl, getMemberDisplayName } from '$utils/room/display';
import { getMxIdLocalPart } from '$utils/matrix';
import { useMatrixClient } from '$hooks/useMatrixClient';
import { useMediaAuthentication } from '$hooks/useMediaAuthentication';
import { useOpenUserRoomProfile } from '$state/hooks/userRoomProfile';
import { useSpaceOptionally } from '$hooks/useSpace';
import { getMouseEventCords } from '$utils/dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { nicknamesAtom } from '$state/nicknames';
import { UserAvatar } from '$components/user-avatar';
import {
  userFallbackIcon,
  ArrowBendUpLeftIcon,
  ChatTeardropDots,
  composerIcon,
  menuIcon,
  Trash,
  X,
} from '$components/icons/phosphor';
import { RenderBody, Time } from '$components/message';
import { useSetting } from '$state/hooks/settings';
import { settingsAtom } from '$state/settings';
import { useCallback, useMemo, useState } from 'react';
import { getReactCustomHtmlParser, LINKIFY_OPTS } from '$plugins/react-custom-html-parser';
import type { Opts as LinkifyOpts } from 'linkifyjs';
import type { HTMLReactParserOptions } from 'html-react-parser';
import { useSpoilerClickHandler } from '$hooks/useSpoilerClickHandler';
import { modalAtom, ModalType } from '$state/modal';
import { roomIdToReplyDraftAtomFamily } from '$state/room/roomInputDrafts';
import { useRoomPermissions } from '$hooks/useRoomPermissions';
import { useRoomCreators } from '$hooks/useRoomCreators';
import { usePowerLevelsContext } from '$hooks/usePowerLevels';

import { useSettingsLinkBaseUrl } from '$features/settings/useSettingsLinkBaseUrl';
import * as css from './EventHistory.css';
import { EventType, RelationType } from '$types/matrix-sdk';

type EventHistoryProps = {
  room: Room;
  mEvents: MatrixEvent[];
  requestClose: () => void;
};
export const EventHistory = as<'div', EventHistoryProps>(
  ({ className, room, mEvents, requestClose, ...props }, ref) => {
    const mx = useMatrixClient();
    const useAuthentication = useMediaAuthentication();
    const settingsLinkBaseUrl = useSettingsLinkBaseUrl();
    const openProfile = useOpenUserRoomProfile();
    const space = useSpaceOptionally();
    const nicknames = useAtomValue(nicknamesAtom);

    const getName = (userId: string) =>
      getMemberDisplayName(room, userId, nicknames) ?? getMxIdLocalPart(userId) ?? userId;

    const readerId = mEvents[0]?.event.sender ?? '';
    const name = getName(readerId ?? '');
    const avatarMxcUrl = room.getMember(readerId ?? '')?.getMxcAvatarUrl();
    const avatarUrl = getAvatarUrl(mx, avatarMxcUrl, 100, useAuthentication);

    const [hour24Clock] = useSetting(settingsAtom, 'hour24Clock');
    const [dateFormatString] = useSetting(settingsAtom, 'dateFormatString');

    const linkifyOpts = useMemo<LinkifyOpts>(() => ({ ...LINKIFY_OPTS }), []);
    const spoilerClickHandler = useSpoilerClickHandler();
    const htmlReactParserOptions = useMemo<HTMLReactParserOptions>(
      () =>
        getReactCustomHtmlParser(mx, mEvents[0]!.getRoomId(), {
          settingsLinkBaseUrl,
          linkifyOpts,
          useAuthentication,
          handleSpoilerClick: spoilerClickHandler,
        }),
      [linkifyOpts, mEvents, mx, settingsLinkBaseUrl, spoilerClickHandler, useAuthentication]
    );
    const powerLevels = usePowerLevelsContext();
    const creators = useRoomCreators(room);
    const permissions = useRoomPermissions(creators, powerLevels);
    const canRedact = permissions.action('redact', mx.getSafeUserId());
    const canDeleteOwn = permissions.event(EventType.RoomRedaction, mx.getSafeUserId());
    const canDelete = canRedact || (canDeleteOwn && mEvents[0]?.getSender() === mx.getUserId());

    const setReplyDraft = useSetAtom(roomIdToReplyDraftAtomFamily(room.roomId));
    const triggerReply = useCallback(
      (replyId: string, startThread = false) => {
        const replyEvt = room.findEventById(replyId);
        if (!replyEvt) return;
        const content: IContent = replyEvt.getOriginalContent();
        const body = content?.['m.new_content']?.body ?? content?.body ?? '';
        const formattedBody =
          content?.['m.new_content']?.formatted_body ?? content?.formatted_body ?? '';
        const { 'm.relates_to': relation } = startThread
          ? { 'm.relates_to': { rel_type: RelationType.Thread, event_id: replyId } }
          : replyEvt.getWireContent();
        const senderId = replyEvt.getSender();
        if (senderId) {
          if (typeof body === 'string') {
            setReplyDraft({
              userId: senderId,
              eventId: replyId,
              body,
              formattedBody,
              relation,
            });
          } else {
            setReplyDraft({
              userId: senderId,
              eventId: replyId,
              body: '',
              formattedBody: '',
              relation,
            });
          }
        }
      },
      [room, setReplyDraft]
    );

    function MenuOptions({ mEvent }: Readonly<{ mEvent: MatrixEvent }>) {
      const setModal = useSetAtom(modalAtom);
      return (
        <Menu className={css.MenuOptions}>
          <MenuItem
            size="300"
            after={menuIcon(ArrowBendUpLeftIcon)}
            radii="300"
            fill="None"
            variant="Secondary"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (mEvent.event.event_id) {
                triggerReply(mEvent.event.event_id, false);
                requestClose();
              }
            }}
          />
          <MenuItem
            size="300"
            after={menuIcon(ChatTeardropDots)}
            radii="300"
            fill="None"
            variant="Secondary"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (mEvent.event.event_id) {
                triggerReply(mEvent.event.event_id, true);
                requestClose();
              }
            }}
          />
          {canDelete && (
            <MenuItem
              size="300"
              after={menuIcon(Trash)}
              radii="300"
              fill="None"
              variant="Critical"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setModal({
                  type: ModalType.Delete,
                  room,
                  mEvent,
                });
              }}
            />
          )}
        </Menu>
      );
    }

    function EventItem({
      mEvent,
      EventContent,
    }: Readonly<{ mEvent: MatrixEvent; EventContent: IContent }>) {
      const [isHovered, setIsHovered] = useState(false);
      return (
        <Box
          style={{ position: 'relative', width: '100%' }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={() => setIsHovered(!isHovered)}
        >
          <Box className={css.EventItem}>
            <Time
              ts={mEvent.getTs()}
              hour24Clock={hour24Clock}
              dateFormatString={dateFormatString}
            />

            <Text size="T400" style={{ paddingLeft: '10px', wordBreak: 'break-word' }}>
              <RenderBody
                body={EventContent?.['m.new_content']?.body ?? EventContent?.body ?? ''}
                customBody={
                  EventContent?.['m.new_content']?.formatted_body ??
                  EventContent?.formatted_body ??
                  ''
                }
                htmlReactParserOptions={htmlReactParserOptions}
                linkifyOpts={linkifyOpts}
              />
            </Text>
          </Box>
          {isHovered && <MenuOptions mEvent={mEvent} />}
        </Box>
      );
    }

    return (
      <Box
        className={classNames(css.EventHistory, className)}
        direction="Column"
        {...props}
        ref={ref}
      >
        <Header className={css.Header} variant="Surface" size="600">
          <Box grow="Yes">
            <Text size="H3">Message version history</Text>
          </Box>
          <IconButton size="300" onClick={requestClose}>
            {composerIcon(X)}
          </IconButton>
        </Header>
        <Header>
          <MenuItem
            key={readerId}
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: `${config.space.S200} ${config.space.S200}`,
              height: 'unset',
            }}
            radii="400"
            onClick={(event) => {
              openProfile(
                room.roomId,
                space?.roomId,
                readerId,
                undefined,
                getMouseEventCords(event.nativeEvent),
                'Bottom'
              );
            }}
            before={
              <Avatar size="300">
                <UserAvatar
                  userId={readerId ?? ''}
                  src={avatarUrl ?? undefined}
                  alt={name}
                  renderFallback={() => userFallbackIcon('sm')}
                />
              </Avatar>
            }
          >
            <Text size="T400">{name}</Text>
          </MenuItem>
        </Header>
        <Box grow="Yes" style={{ overflow: 'scroll' }}>
          <Scroll visibility="Hover">
            <Box className={css.Content} direction="Column">
              {mEvents.map((mEvent) => {
                if (!mEvent.event.sender) return <div key={mEvent.event.event_id} />;
                const EventContent = mEvent.getOriginalContent();
                return (
                  <>
                    <hr
                      style={{
                        width: '100%',
                        color: color.Surface.ContainerLine,
                      }}
                    />
                    <EventItem mEvent={mEvent} EventContent={EventContent} />
                  </>
                );
              })}
            </Box>
          </Scroll>
        </Box>
      </Box>
    );
  }
);
